import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ApiURl from "../controllers/Api";
import Logo from "../images/logo.png";

/* ============================================================
   SL GOLD THEME
   ============================================================ */

const PAGE_BG = "var(--user-bg, #E8D9D5)";
const MAROON = "var(--user-maroon, #2B0000)";
const GOLD = "var(--user-gold, #D4AF37)";

const CREAM = "#FBF7F2";
const SOFT_CREAM = "#F6EFEA";

const BORDER = "rgba(43,0,0,.12)";
const GOLD_BORDER = "rgba(212,175,55,.30)";
const TEXT_SOFT = "rgba(43,0,0,.62)";
const TEXT_LIGHT = "rgba(43,0,0,.45)";

const Receipt = () => {
  const { order_id } = useParams();
  const navigate = useNavigate();

  const [orderDetails, setOrderDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);

  /* ============================================================
     FETCH ORDER
     ============================================================ */

  useEffect(() => {
    const fetchOrderDetails = async () => {
      try {
        const user_id = localStorage.getItem("user_id");

        if (!user_id) {
          setError("User ID is missing. Please log in.");
          setLoading(false);
          return;
        }

        const response = await fetch(
          `${ApiURl}/getOrderDetails.php?order_id=${order_id}&user_id=${user_id}`
        );

        if (!response.ok) {
          throw new Error("Failed to fetch order details");
        }

        const data = await response.json();

        if (data.success) {
          setOrderDetails(data.data[0]);
        } else {
          throw new Error(data.message || "No data available");
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [order_id]);

  /* ============================================================
     COUPON CALCULATION
     ============================================================ */

  const resolveCouponDiscount = (info, subtotal) => {
    if (!info || subtotal <= 0) return 0;

    const couponId = Number(info?.coupon_id || 0);

    const hasCouponCode =
      info?.coupon_code &&
      String(info.coupon_code).trim() !== "" &&
      info?.coupon_code !== "N/A";

    if (!couponId && !hasCouponCode) return 0;

    const couponType = String(
      info?.coupon_type || info?.discount_type || ""
    ).toLowerCase();

    const fixedCandidates = [
      info?.coupon_value,
      info?.coupon_amount,
      info?.discount_amount,
      info?.discount_value,
      info?.applied_discount,
      info?.flat_discount,
    ];

    const percentageCandidates = [
      info?.coupon_percentage,
      info?.discount_percentage,
      info?.percentage,
    ];

    const fallbackCandidates = [info?.coupon_discount];

    const firstValid = (arr) => {
      for (const val of arr) {
        const num = Number(val);

        if (!Number.isNaN(num) && num > 0) {
          return num;
        }
      }

      return 0;
    };

    const fixedValue = firstValid(fixedCandidates);
    const percentageValue = firstValid(percentageCandidates);
    const fallbackValue = firstValid(fallbackCandidates);

    let discount = 0;

    if (couponType.includes("percent")) {
      discount = (subtotal * percentageValue) / 100;
    } else if (
      couponType.includes("fixed") ||
      couponType.includes("flat")
    ) {
      discount = fixedValue || fallbackValue;
    } else {
      discount =
        fixedValue ||
        (percentageValue
          ? (subtotal * percentageValue) / 100
          : fallbackValue);
    }

    return Math.min(subtotal, Number(discount || 0));
  };

  /* ============================================================
     PRICE BREAKDOWN
     ============================================================ */

  const priceBreakdown = useMemo(() => {
    const empty = {
      subtotal: 0,
      discountAmount: 0,
      discountedSubtotal: 0,

      totalCgst: 0,
      totalSgst: 0,
      totalIgst: 0,
      totalTax: 0,

      grandTotalAfterDiscount: 0,

      roundedTotal: 0,
      roundingDiff: 0,

      itemWiseBreakdown: [],
    };

    if (
      !orderDetails ||
      !Array.isArray(orderDetails.products)
    ) {
      return empty;
    }

    const products = orderDetails.products;

    const safeProducts = products.map((item) => {
      const quantity = Number(item.quantity || 0);
      const price = Number(item.price || 0);

      const itemSubtotal = price * quantity;

      const cgst = Number(item.cgst || 0);
      const sgst = Number(item.sgst || 0);
      const igst = Number(item.igst || 0);

      const cgstRate =
        itemSubtotal > 0 ? cgst / itemSubtotal : 0;

      const sgstRate =
        itemSubtotal > 0 ? sgst / itemSubtotal : 0;

      const igstRate =
        itemSubtotal > 0 ? igst / itemSubtotal : 0;

      return {
        ...item,
        quantity,
        price,
        itemSubtotal,
        cgst,
        sgst,
        igst,
        cgstRate,
        sgstRate,
        igstRate,
      };
    });

    const subtotal = safeProducts.reduce(
      (sum, item) => sum + item.itemSubtotal,
      0
    );

    const discountAmount = resolveCouponDiscount(
      orderDetails,
      subtotal
    );

    const discountedSubtotal = Math.max(
      0,
      subtotal - discountAmount
    );

    let totalCgst = 0;
    let totalSgst = 0;
    let totalIgst = 0;

    const itemWiseBreakdown = safeProducts.map((item) => {
      const discountShare =
        subtotal > 0
          ? (item.itemSubtotal / subtotal) * discountAmount
          : 0;

      const taxableAfterDiscount = Math.max(
        0,
        item.itemSubtotal - discountShare
      );

      const recalculatedCgst =
        taxableAfterDiscount * item.cgstRate;

      const recalculatedSgst =
        taxableAfterDiscount * item.sgstRate;

      const recalculatedIgst =
        taxableAfterDiscount * item.igstRate;

      totalCgst += recalculatedCgst;
      totalSgst += recalculatedSgst;
      totalIgst += recalculatedIgst;

      const discountedUnitPrice =
        item.quantity > 0
          ? taxableAfterDiscount / item.quantity
          : 0;

      return {
        name: item.name || "Unknown",
        product_id: item.product_id,
        size: item.size || "N/A",
        quantity: item.quantity,

        price: Number(discountedUnitPrice.toFixed(2)),

        total_amount: Number(
          taxableAfterDiscount.toFixed(2)
        ),

        cgst: Number(recalculatedCgst.toFixed(2)),
        sgst: Number(recalculatedSgst.toFixed(2)),
        igst: Number(recalculatedIgst.toFixed(2)),

        grandtotal: Number(
          (
            taxableAfterDiscount +
            recalculatedCgst +
            recalculatedSgst +
            recalculatedIgst
          ).toFixed(2)
        ),
      };
    });

    const totalTax =
      totalCgst + totalSgst + totalIgst;

    const grandTotalAfterDiscount =
      discountedSubtotal + totalTax;

    const roundedTotal = Math.round(
      grandTotalAfterDiscount
    );

    const roundingDiff =
      roundedTotal - grandTotalAfterDiscount;

    return {
      subtotal: Number(subtotal.toFixed(2)),

      discountAmount: Number(
        discountAmount.toFixed(2)
      ),

      discountedSubtotal: Number(
        discountedSubtotal.toFixed(2)
      ),

      totalCgst: Number(totalCgst.toFixed(2)),
      totalSgst: Number(totalSgst.toFixed(2)),
      totalIgst: Number(totalIgst.toFixed(2)),

      totalTax: Number(totalTax.toFixed(2)),

      grandTotalAfterDiscount: Number(
        grandTotalAfterDiscount.toFixed(2)
      ),

      roundedTotal: Number(roundedTotal.toFixed(2)),

      roundingDiff: Number(
        roundingDiff.toFixed(2)
      ),

      itemWiseBreakdown,
    };
  }, [orderDetails]);

  const hasDiscount = useMemo(() => {
    return priceBreakdown.discountAmount > 0;
  }, [priceBreakdown.discountAmount]);

  /* ============================================================
     DOWNLOAD HTML RECEIPT
     ============================================================ */

  const handleDownload = () => {
    setDownloading(true);

    try {
      const receiptElement =
        document.getElementById("receipt-content");

      if (!receiptElement) {
        throw new Error("Receipt content not found");
      }

      const receiptContent = receiptElement.innerHTML;

      const fullHtml = `
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="UTF-8" />

            <title>Receipt_${order_id}</title>

            <style>

              * {
                box-sizing: border-box;
              }

              body {
                font-family: Arial, sans-serif;
                margin: 0;
                padding: 30px;
                color: #2B0000;
                background: #ffffff;
              }

              table {
                border-collapse: collapse;
                width: 100%;
              }

              th {
                background: #2B0000 !important;
                color: #D4AF37 !important;
                padding: 10px;
                font-size: 11px;
                text-align: left;
              }

              td {
                padding: 10px;
                border-bottom: 1px solid rgba(43,0,0,.12);
                font-size: 11px;
              }

              .text-center {
                text-align: center;
              }

              .text-right {
                text-align: right;
              }

            </style>
          </head>

          <body>
            ${receiptContent}
          </body>
        </html>
      `;

      const blob = new Blob([fullHtml], {
        type: "text/html",
      });

      const link = document.createElement("a");

      link.href = URL.createObjectURL(blob);

      link.download = `Receipt_${order_id}.html`;

      link.click();

      URL.revokeObjectURL(link.href);
    } catch (err) {
      console.error("Download error:", err);
    } finally {
      setDownloading(false);
    }
  };

  /* ============================================================
     PDF / SERVER INVOICE
     ============================================================ */

  const handlePrint = () => {
    const user_id = localStorage.getItem("user_id");

    if (!user_id) {
      setError("User ID is missing. Please log in.");
      return;
    }

    window.open(
      `${ApiURl}/download_invoice.php?invoice_number=${order_id}`,
      "_blank"
    );
  };

  /* ============================================================
     FORMAT DATE
     ============================================================ */

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";

    try {
      const date = new Date(dateString);

      return date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateString;
    }
  };

  /* ============================================================
     STATUS
     ============================================================ */

  const getStatusStyle = (status) => {
    const s = String(status || "").toLowerCase();

    if (s === "completed" || s === "delivered") {
      return {
        color: "#477B60",
        backgroundColor: "rgba(71,123,96,.09)",
        borderColor: "rgba(71,123,96,.25)",
      };
    }

    if (s === "pending") {
      return {
        color: "#856713",
        backgroundColor: "rgba(212,175,55,.12)",
        borderColor: "rgba(212,175,55,.35)",
      };
    }

    if (s === "cancelled") {
      return {
        color: MAROON,
        backgroundColor: "rgba(43,0,0,.06)",
        borderColor: "rgba(43,0,0,.18)",
      };
    }

    return {
      color: MAROON,
      backgroundColor: "rgba(212,175,55,.10)",
      borderColor: GOLD_BORDER,
    };
  };

  /* ============================================================
     LOADING
     ============================================================ */

  if (loading) {
    return (
      <section
        className="
          flex
          min-h-screen
          items-center
          justify-center
          px-4
        "
        style={{
          backgroundColor: PAGE_BG,
        }}
      >
        <div
          className="
            w-full
            max-w-[470px]
            rounded-[22px]
            border
            p-8
            text-center
          "
          style={{
            backgroundColor: CREAM,
            borderColor: GOLD_BORDER,
          }}
        >
          <div
            className="
              mx-auto
              h-12
              w-12
              animate-spin
              rounded-full
              border-[3px]
              border-r-transparent
            "
            style={{
              borderColor: GOLD,
              borderRightColor: "transparent",
            }}
          />

          <h2
            className="
              mt-5
              font-serif
              text-[22px]
            "
            style={{
              color: MAROON,
            }}
          >
            Preparing Your Invoice
          </h2>

          <p
            className="
              mt-1
              text-[11px]
            "
            style={{
              color: TEXT_LIGHT,
            }}
          >
            Please wait while we fetch your receipt.
          </p>
        </div>
      </section>
    );
  }

  /* ============================================================
     ERROR
     ============================================================ */

  if (error || !orderDetails) {
    return (
      <section
        className="
          flex
          min-h-screen
          items-center
          justify-center
          px-4
        "
        style={{
          backgroundColor: PAGE_BG,
        }}
      >
        <div
          className="
            w-full
            max-w-[470px]
            rounded-[22px]
            border
            p-8
            text-center
          "
          style={{
            backgroundColor: CREAM,
            borderColor: GOLD_BORDER,
          }}
        >
          <div
            className="
              mx-auto
              flex
              h-14
              w-14
              items-center
              justify-center
              rounded-full
              font-serif
              text-[24px]
            "
            style={{
              backgroundColor: MAROON,
              color: GOLD,
            }}
          >
            !
          </div>

          <h2
            className="
              mt-5
              font-serif
              text-[25px]
              font-medium
            "
            style={{
              color: MAROON,
            }}
          >
            {error
              ? "Error Loading Receipt"
              : "No Receipt Found"}
          </h2>

          <p
            className="
              mt-2
              text-[12px]
            "
            style={{
              color: TEXT_SOFT,
            }}
          >
            {error ||
              `We couldn't find a receipt for order #${order_id}.`}
          </p>

          <button
            onClick={() => navigate("/order-section")}
            className="
              mt-6
              rounded-full
              px-6
              py-3
              text-[10px]
              font-semibold
              uppercase
              tracking-[.14em]
            "
            style={{
              backgroundColor: MAROON,
              color: GOLD,
            }}
          >
            ← Back to Orders
          </button>
        </div>
      </section>
    );
  }

  /* ============================================================
     DATA
     ============================================================ */

  const {
    order_id: orderId,
    date,
    status,
    address_line1,
    address_line2,
    landmark,
    city,
    state,
    phone,
  } = orderDetails;

  const formattedAddress = [
    address_line1,
    address_line2,
    landmark,
    city,
    state,
  ]
    .filter(Boolean)
    .join(", ");

  /* ============================================================
     MAIN
     ============================================================ */

  return (
    <main
      className="
        min-h-screen
        px-3
        py-10
        sm:px-6
        sm:py-14
        lg:px-10
        lg:py-16
      "
      style={{
        backgroundColor: PAGE_BG,
      }}
    >
      <div className="mx-auto max-w-[1200px]">

        {/* ======================================================
            PAGE TITLE
            ====================================================== */}

        <header className="mb-7 text-center">

          <div
            className="
              mb-3
              flex
              items-center
              justify-center
              gap-3
            "
          >
            <span
              className="h-px w-8"
              style={{
                backgroundColor: MAROON,
                opacity: 0.45,
              }}
            />

            <span
              className="
                text-[9px]
                font-semibold
                uppercase
                tracking-[.28em]
              "
              style={{
                color: MAROON,
              }}
            >
              SL Gold
            </span>

            <span
              className="h-px w-8"
              style={{
                backgroundColor: MAROON,
                opacity: 0.45,
              }}
            />
          </div>

          <h1
            className="
              font-serif
              text-[34px]
              font-medium
              tracking-[-.025em]
              sm:text-[40px]
              lg:text-[46px]
            "
            style={{
              color: MAROON,
            }}
          >
            Tax Invoice
          </h1>

          <p
            className="
              mt-2
              text-[11px]
              sm:text-[12px]
            "
            style={{
              color: TEXT_SOFT,
            }}
          >
            Order #{orderId} · {formatDate(date)}
          </p>

          <button
            onClick={() => navigate("/order-section")}
            className="
              mt-5
              rounded-full
              border
              px-5
              py-2.5
              text-[9px]
              font-semibold
              uppercase
              tracking-[.13em]
            "
            style={{
              color: MAROON,
              borderColor: GOLD_BORDER,
              backgroundColor: "rgba(251,247,242,.5)",
            }}
          >
            ← Back to Orders
          </button>

        </header>

        {/* ======================================================
            INVOICE
            ====================================================== */}

        <article
          className="
            overflow-hidden
            rounded-[22px]
            border
          "
          style={{
            backgroundColor: CREAM,
            borderColor: GOLD_BORDER,
          }}
        >

          {/* ====================================================
              MAROON HEADER
              ==================================================== */}

          <div
            className="
              px-5
              py-5
              sm:px-7
              sm:py-6
            "
            style={{
              backgroundColor: MAROON,
            }}
          >
            <div
              className="
                flex
                flex-col
                gap-5
                sm:flex-row
                sm:items-center
                sm:justify-between
              "
            >

              <div className="flex items-center gap-3">

                <div
                  className="
                    flex
                    h-12
                    w-12
                    items-center
                    justify-center
                    rounded-full
                    border
                    p-1
                  "
                  style={{
                    borderColor: GOLD_BORDER,
                    backgroundColor: "rgba(255,255,255,.06)",
                  }}
                >
                  <img
                    src={Logo}
                    alt="SL Gold"
                    className="
                      h-full
                      w-full
                      object-contain
                    "
                  />
                </div>

                <div>
                  <p
                    className="
                      text-[8px]
                      font-semibold
                      uppercase
                      tracking-[.22em]
                    "
                    style={{
                      color: GOLD,
                    }}
                  >
                    Official Invoice
                  </p>

                  <h2
                    className="
                      mt-0.5
                      font-serif
                      text-[23px]
                      font-medium
                      text-white
                    "
                  >
                    SL GOLD
                  </h2>

                  <p
                    className="
                      text-[10px]
                    "
                    style={{
                      color: "rgba(255,255,255,.58)",
                    }}
                  >
                    Timeless beauty. Legacy of gold.
                  </p>
                </div>

              </div>

              <div
                className="
                  flex
                  items-center
                  justify-between
                  gap-5
                  sm:justify-end
                "
              >

                <span
                  className="
                    inline-flex
                    rounded-full
                    border
                    px-3
                    py-1.5
                    text-[9px]
                    font-semibold
                    uppercase
                    tracking-[.1em]
                  "
                  style={getStatusStyle(status)}
                >
                  {status}
                </span>

                <div className="text-right">

                  <p
                    className="text-[9px]"
                    style={{
                      color: "rgba(255,255,255,.55)",
                    }}
                  >
                    Total Amount
                  </p>

                  <p
                    className="
                      font-serif
                      text-[24px]
                      font-semibold
                    "
                    style={{
                      color: GOLD,
                    }}
                  >
                    ₹{priceBreakdown.roundedTotal.toFixed(2)}
                  </p>

                </div>

              </div>

            </div>
          </div>

          {/* ====================================================
              RECEIPT CONTENT
              ==================================================== */}

          <div
            id="receipt-content"
            className="
              p-4
              sm:p-6
              lg:p-7
            "
          >

            {/* ==================================================
                INFO CARDS
                ================================================== */}

            <div
              className="
                grid
                gap-3
                md:grid-cols-2
              "
            >

              {/* ORDER INFO */}

              <div
                className="
                  rounded-[16px]
                  border
                  p-4
                  sm:p-5
                "
                style={{
                  backgroundColor: SOFT_CREAM,
                  borderColor: BORDER,
                }}
              >

                <p
                  className="
                    text-[8px]
                    font-semibold
                    uppercase
                    tracking-[.18em]
                  "
                  style={{
                    color: GOLD,
                  }}
                >
                  Invoice Information
                </p>

                <h3
                  className="
                    mt-1
                    font-serif
                    text-[19px]
                    font-medium
                  "
                  style={{
                    color: MAROON,
                  }}
                >
                  Order Details
                </h3>

                <div
                  className="
                    mt-4
                    space-y-3
                    border-t
                    pt-4
                  "
                  style={{
                    borderColor: BORDER,
                  }}
                >

                  <InfoRow
                    label="Order ID"
                    value={`#${orderId}`}
                  />

                  <InfoRow
                    label="Date"
                    value={formatDate(date)}
                  />

                  <InfoRow
                    label="Status"
                    value={status || "N/A"}
                  />

                </div>

              </div>

              {/* SHIPPING */}

              <div
                className="
                  rounded-[16px]
                  border
                  p-4
                  sm:p-5
                "
                style={{
                  backgroundColor: MAROON,
                  borderColor: GOLD_BORDER,
                }}
              >

                <p
                  className="
                    text-[8px]
                    font-semibold
                    uppercase
                    tracking-[.18em]
                  "
                  style={{
                    color: GOLD,
                  }}
                >
                  Delivery
                </p>

                <h3
                  className="
                    mt-1
                    font-serif
                    text-[19px]
                    font-medium
                    text-white
                  "
                >
                  Shipping Address
                </h3>

                <div
                  className="
                    mt-4
                    border-t
                    pt-4
                    text-[11px]
                    leading-[1.8]
                  "
                  style={{
                    borderColor: "rgba(212,175,55,.22)",
                    color: "rgba(255,255,255,.72)",
                  }}
                >

                  <p>
                    {formattedAddress || "Address unavailable"}
                  </p>

                  <p
                    className="mt-2"
                    style={{
                      color: GOLD,
                    }}
                  >
                    Phone: {phone || "N/A"}
                  </p>

                </div>

              </div>

            </div>

            {/* ==================================================
                PRODUCTS HEADING
                ================================================== */}

            <div
              className="
                mt-7
                flex
                items-end
                justify-between
                border-b
                pb-3
              "
              style={{
                borderColor: BORDER,
              }}
            >

              <div>

                <p
                  className="
                    text-[8px]
                    font-semibold
                    uppercase
                    tracking-[.18em]
                  "
                  style={{
                    color: GOLD,
                  }}
                >
                  Your Selection
                </p>

                <h3
                  className="
                    mt-1
                    font-serif
                    text-[21px]
                    font-medium
                  "
                  style={{
                    color: MAROON,
                  }}
                >
                  Products
                </h3>

              </div>

              <span
                className="
                  text-[9px]
                  uppercase
                  tracking-[.1em]
                "
                style={{
                  color: TEXT_LIGHT,
                }}
              >
                {priceBreakdown.itemWiseBreakdown.length}{" "}
                {priceBreakdown.itemWiseBreakdown.length === 1
                  ? "Item"
                  : "Items"}
              </span>

            </div>

            {/* ==================================================
                DESKTOP TABLE
                ================================================== */}

            <div
              className="
                mt-4
                hidden
                overflow-x-auto
                rounded-[14px]
                border
                md:block
              "
              style={{
                borderColor: BORDER,
              }}
            >

              <table className="min-w-full">

                <thead>
                  <tr
                    style={{
                      backgroundColor: MAROON,
                    }}
                  >

                    <InvoiceTH>
                      Product
                    </InvoiceTH>

                    <InvoiceTH center>
                      Qty
                    </InvoiceTH>

                    <InvoiceTH center>
                      Size
                    </InvoiceTH>

                    <InvoiceTH right>
                      Price
                    </InvoiceTH>

                    <InvoiceTH right>
                      Total
                    </InvoiceTH>

                    <InvoiceTH right>
                      CGST
                    </InvoiceTH>

                    <InvoiceTH right>
                      SGST
                    </InvoiceTH>

                    <InvoiceTH right>
                      IGST
                    </InvoiceTH>

                    <InvoiceTH right>
                      Grand Total
                    </InvoiceTH>

                  </tr>
                </thead>

                <tbody>

                  {priceBreakdown.itemWiseBreakdown.length > 0 ? (
                    priceBreakdown.itemWiseBreakdown.map(
                      (product, index) => (
                        <tr
                          key={index}
                          className="border-t"
                          style={{
                            borderColor: BORDER,
                          }}
                        >

                          <InvoiceTD>
                            {product.name}
                          </InvoiceTD>

                          <InvoiceTD center>
                            {product.quantity}
                          </InvoiceTD>

                          <InvoiceTD center>
                            {product.size || "N/A"}
                          </InvoiceTD>

                          <InvoiceTD right>
                            ₹{(product.price || 0).toFixed(2)}
                          </InvoiceTD>

                          <InvoiceTD right>
                            ₹{(product.total_amount || 0).toFixed(2)}
                          </InvoiceTD>

                          <InvoiceTD right>
                            ₹{(product.cgst || 0).toFixed(2)}
                          </InvoiceTD>

                          <InvoiceTD right>
                            ₹{(product.sgst || 0).toFixed(2)}
                          </InvoiceTD>

                          <InvoiceTD right>
                            ₹{(product.igst || 0).toFixed(2)}
                          </InvoiceTD>

                          <InvoiceTD right strong>
                            ₹{(product.grandtotal || 0).toFixed(2)}
                          </InvoiceTD>

                        </tr>
                      )
                    )
                  ) : (
                    <tr>
                      <td
                        colSpan="9"
                        className="
                          px-4
                          py-8
                          text-center
                          text-[11px]
                        "
                        style={{
                          color: TEXT_LIGHT,
                        }}
                      >
                        No products available.
                      </td>
                    </tr>
                  )}

                </tbody>

              </table>

            </div>

            {/* ==================================================
                MOBILE PRODUCT CARDS
                ================================================== */}

            <div
              className="
                mt-4
                space-y-2
                md:hidden
              "
            >

              {priceBreakdown.itemWiseBreakdown.length > 0 ? (
                priceBreakdown.itemWiseBreakdown.map(
                  (product, index) => {

                    const tax =
                      Number(product.cgst || 0) +
                      Number(product.sgst || 0) +
                      Number(product.igst || 0);

                    return (
                      <article
                        key={index}
                        className="
                          rounded-[14px]
                          border
                          p-4
                        "
                        style={{
                          backgroundColor: SOFT_CREAM,
                          borderColor: BORDER,
                        }}
                      >

                        <div
                          className="
                            flex
                            items-start
                            justify-between
                            gap-3
                          "
                        >

                          <div className="min-w-0">

                            <p
                              className="
                                font-serif
                                text-[16px]
                                font-medium
                                leading-tight
                              "
                              style={{
                                color: MAROON,
                              }}
                            >
                              {product.name}
                            </p>

                            <p
                              className="
                                mt-1
                                text-[9px]
                              "
                              style={{
                                color: TEXT_LIGHT,
                              }}
                            >
                              Qty {product.quantity} · Size{" "}
                              {product.size || "N/A"}
                            </p>

                          </div>

                          <p
                            className="
                              flex-shrink-0
                              font-serif
                              text-[16px]
                              font-semibold
                            "
                            style={{
                              color: MAROON,
                            }}
                          >
                            ₹{(product.grandtotal || 0).toFixed(2)}
                          </p>

                        </div>

                        <div
                          className="
                            mt-3
                            grid
                            grid-cols-2
                            gap-x-4
                            gap-y-2
                            border-t
                            pt-3
                          "
                          style={{
                            borderColor: BORDER,
                          }}
                        >

                          <MobileValue
                            label="Unit Price"
                            value={`₹${(product.price || 0).toFixed(2)}`}
                          />

                          <MobileValue
                            label="Taxable"
                            value={`₹${(product.total_amount || 0).toFixed(2)}`}
                          />

                          <MobileValue
                            label="Tax"
                            value={`₹${tax.toFixed(2)}`}
                          />

                          <MobileValue
                            label="Total"
                            value={`₹${(product.grandtotal || 0).toFixed(2)}`}
                            strong
                          />

                        </div>

                      </article>
                    );
                  }
                )
              ) : (
                <p
                  className="
                    py-6
                    text-center
                    text-[11px]
                  "
                  style={{
                    color: TEXT_LIGHT,
                  }}
                >
                  No products available.
                </p>
              )}

            </div>

            {/* ==================================================
                TAX SUMMARY
                ================================================== */}

            <div
              className="
                mt-7
                grid
                gap-3
                lg:grid-cols-[1fr_390px]
              "
            >

              {/* TAX CARDS */}

              <div
                className="
                  grid
                  grid-cols-2
                  gap-2
                  sm:grid-cols-4
                "
              >

                <TaxCard
                  label="CGST"
                  value={`₹${priceBreakdown.totalCgst.toFixed(2)}`}
                />

                <TaxCard
                  label="SGST"
                  value={`₹${priceBreakdown.totalSgst.toFixed(2)}`}
                />

                <TaxCard
                  label="IGST"
                  value={`₹${priceBreakdown.totalIgst.toFixed(2)}`}
                />

                <TaxCard
                  label="Total Tax"
                  value={`₹${priceBreakdown.totalTax.toFixed(2)}`}
                />

              </div>

              {/* TOTAL BOX */}

              <div
                className="
                  rounded-[16px]
                  border
                  p-4
                  sm:p-5
                "
                style={{
                  backgroundColor: MAROON,
                  borderColor: GOLD_BORDER,
                }}
              >

                <TotalRow
                  label="Subtotal"
                  value={`₹${priceBreakdown.grandTotalAfterDiscount.toFixed(
                    2
                  )}`}
                />

                <TotalRow
                  label="Rounding Difference"
                  value={`₹${priceBreakdown.roundingDiff.toFixed(2)}`}
                />

                <div
                  className="
                    mt-3
                    flex
                    items-end
                    justify-between
                    gap-4
                    border-t
                    pt-4
                  "
                  style={{
                    borderColor: "rgba(212,175,55,.25)",
                  }}
                >

                  <div>

                    <p
                      className="
                        text-[8px]
                        uppercase
                        tracking-[.16em]
                      "
                      style={{
                        color: "rgba(255,255,255,.55)",
                      }}
                    >
                      Total Amount
                    </p>

                    <p
                      className="
                        mt-1
                        text-[9px]
                      "
                      style={{
                        color: "rgba(255,255,255,.42)",
                      }}
                    >
                      Inclusive of applicable taxes
                    </p>

                  </div>

                  <p
                    className="
                      font-serif
                      text-[25px]
                      font-semibold
                    "
                    style={{
                      color: GOLD,
                    }}
                  >
                    ₹{priceBreakdown.roundedTotal.toFixed(2)}
                  </p>

                </div>

              </div>

            </div>

            {/* ==================================================
                THANK YOU
                ================================================== */}

            <div
              className="
                mt-7
                border-t
                pt-6
                text-center
              "
              style={{
                borderColor: BORDER,
              }}
            >

              <p
                className="
                  text-[8px]
                  font-semibold
                  uppercase
                  tracking-[.24em]
                "
                style={{
                  color: GOLD,
                }}
              >
                SL Gold
              </p>

              <p
                className="
                  mt-2
                  font-serif
                  text-[19px]
                  italic
                "
                style={{
                  color: MAROON,
                }}
              >
                Thank you for shopping with us.
              </p>

              <p
                className="
                  mx-auto
                  mt-1
                  max-w-[450px]
                  text-[9px]
                  leading-relaxed
                "
                style={{
                  color: TEXT_LIGHT,
                }}
              >
                We hope your SL Gold piece becomes part of
                your most treasured moments.
              </p>

            </div>

          </div>

          {/* ====================================================
              ACTION BAR
              ==================================================== */}

          <footer
            className="
              border-t
              px-4
              py-4
              sm:px-6
            "
            style={{
              backgroundColor: SOFT_CREAM,
              borderColor: BORDER,
            }}
          >

            <div
              className="
                flex
                flex-col
                gap-2
                sm:flex-row
                sm:justify-end
              "
            >

              <button
                onClick={handleDownload}
                disabled={downloading}
                className="
                  rounded-full
                  border
                  px-5
                  py-2.5
                  text-[9px]
                  font-semibold
                  uppercase
                  tracking-[.12em]
                  disabled:opacity-50
                "
                style={{
                  color: MAROON,
                  borderColor: GOLD_BORDER,
                  backgroundColor: CREAM,
                }}
              >
                {downloading
                  ? "Preparing..."
                  : "Save HTML Receipt"}
              </button>

              <button
                onClick={handlePrint}
                disabled={downloading}
                className="
                  rounded-full
                  px-6
                  py-2.5
                  text-[9px]
                  font-semibold
                  uppercase
                  tracking-[.12em]
                  disabled:opacity-50
                "
                style={{
                  backgroundColor: MAROON,
                  color: GOLD,
                }}
              >
                Download Invoice
              </button>

            </div>

          </footer>

        </article>

      </div>
    </main>
  );
};

/* ============================================================
   SMALL COMPONENTS
   ============================================================ */

const InfoRow = ({ label, value }) => (
  <div
    className="
      flex
      items-start
      justify-between
      gap-5
    "
  >
    <span
      className="text-[10px]"
      style={{
        color: TEXT_LIGHT,
      }}
    >
      {label}
    </span>

    <span
      className="
        text-right
        text-[11px]
        font-medium
      "
      style={{
        color: MAROON,
      }}
    >
      {value}
    </span>
  </div>
);

const InvoiceTH = ({
  children,
  center = false,
  right = false,
}) => (
  <th
    className={`
      whitespace-nowrap
      px-3
      py-3
      text-[8px]
      font-semibold
      uppercase
      tracking-[.1em]

      ${
        center
          ? "text-center"
          : right
          ? "text-right"
          : "text-left"
      }
    `}
    style={{
      color: GOLD,
    }}
  >
    {children}
  </th>
);

const InvoiceTD = ({
  children,
  center = false,
  right = false,
  strong = false,
}) => (
  <td
    className={`
      px-3
      py-3
      text-[10px]

      ${
        center
          ? "text-center"
          : right
          ? "text-right"
          : "text-left"
      }

      ${strong ? "font-semibold" : ""}
    `}
    style={{
      color: strong ? MAROON : TEXT_SOFT,
    }}
  >
    {children}
  </td>
);

const MobileValue = ({
  label,
  value,
  strong = false,
}) => (
  <div>
    <p
      className="
        text-[8px]
        uppercase
        tracking-[.1em]
      "
      style={{
        color: TEXT_LIGHT,
      }}
    >
      {label}
    </p>

    <p
      className="
        mt-0.5
        text-[10px]
        font-medium
      "
      style={{
        color: strong ? MAROON : TEXT_SOFT,
      }}
    >
      {value}
    </p>
  </div>
);

const TaxCard = ({ label, value }) => (
  <div
    className="
      rounded-[13px]
      border
      p-3
    "
    style={{
      backgroundColor: SOFT_CREAM,
      borderColor: BORDER,
    }}
  >
    <p
      className="
        text-[8px]
        uppercase
        tracking-[.12em]
      "
      style={{
        color: TEXT_LIGHT,
      }}
    >
      {label}
    </p>

    <p
      className="
        mt-1
        font-serif
        text-[15px]
        font-semibold
      "
      style={{
        color: MAROON,
      }}
    >
      {value}
    </p>
  </div>
);

const TotalRow = ({ label, value }) => (
  <div
    className="
      mb-2
      flex
      items-center
      justify-between
      gap-4
    "
  >
    <span
      className="text-[9px]"
      style={{
        color: "rgba(255,255,255,.55)",
      }}
    >
      {label}
    </span>

    <span
      className="
        text-[10px]
        font-medium
        text-white
      "
    >
      {value}
    </span>
  </div>
);

export default Receipt;